import React from 'react';
import styled, { useTheme } from 'styled-components';
import { useHistory, useParams } from 'react-router';
import Loader from 'react-loading';

import { currencyNumberFormat } from '../../utilities/number-format';
import { LeftArrow2 } from '../../assets/icons';
import useInvoiceDetails from './hooks/use-invoice-details';
import LineItemsTable from './components/LineItemsTable';
import DatesInfo from './components/DatesInfo';
import PayerInfo from './components/PayerInfo';
import PayeeInfo from './components/PayeeInfo';

import { OutlineButton } from '../../components/Buttons';

interface URLParams {
  id: string;
}

const InvoiceDetails = () => {
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams<URLParams>();
  const { details, loading } = useInvoiceDetails(id);

  if (loading || !details) {
    return <StyledLoader type="spin" color={theme.colors.yellow} height={150} width={150} />;
  }

  return (
    <Container>
      <Header>Invoice # {details.invoiceNumber}</Header>
      <BalanceDescription>
        Balance due {currencyNumberFormat(details.balanceDueAmount)}
      </BalanceDescription>

      <StyledPayeeInfo
        name={details.organizationName}
        address={details.organizationAddress}
        taxId={details.organizationTaxId}
        clinicLocation={details.clinicLocation}
      />

      <StyledPayerInfo
        name={details.billTo.patientName}
        addressLine1={details.billTo.addressLine1}
        addressLine2={details.billTo.addressLine2}
        city={details.billTo.city}
        state={details.billTo.state}
        zipCode={details.billTo.zipCode}
      />

      <StyledDatesInfo
        invoiceDate={details.invoiceDate}
        terms={details.terms}
        dueDate={details.invoiceDueDate}
        datesOfService={details.datesOfService}
      />

      <StyledLineItemsTable
        isAzInvoice={details.isAzInvoice}
        lineItems={details.lineItems}
        lineItemsTotalAmount={details.lineItemsTotalAmount}
        invoiceAdjustmentsAmount={details.invoiceAdjustmentsAmount}
        balanceDueAmount={details.balanceDueAmount}
        kindbodyRateAmount={details.kindbodyRateAmount}
        convertedCreditsAmount={details.covertedCreditsAmount}
        insuranceAdjustmentsAmount={details.insuranceAdjustmentsAmount}
        payments={details.payments}
        discount={details.discount}
        appliedCredits={details.appliedCredits}
      />

      <BackButton onClick={() => history.goBack()}>
        <LeftArrow2 />
        Back
      </BackButton>
    </Container>
  );
};

export default InvoiceDetails;

const StyledLoader = styled(Loader)`
  margin: 120px auto auto auto;
`;

const Container = styled.div`
  ${({ theme }) => theme.grid.pageContainer};
  width: 100%;
  max-width: 700px;
  margin-top: 100px;
  display: flex;
  flex-direction: column;

  @media ${({ theme }) => theme.queries.minTablet} {
    margin-top: 90px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-template-areas:
      'header header'
      'balance balance'
      'payee payee'
      'payer dates'
      'lineitems lineitems'
      'back back';
  }
`;

const Header = styled.h1`
  grid-area: header;
  margin: 0;

  font-size: 18px;
  text-align: end;
  font-weight: 700;
`;

const BalanceDescription = styled.p`
  grid-area: balance;
  text-align: end;
`;

const StyledPayeeInfo = styled(PayeeInfo)`
  grid-area: payee;
`;

const StyledPayerInfo = styled(PayerInfo)`
  grid-area: payer;
`;

const StyledDatesInfo = styled(DatesInfo)`
  grid-area: dates;
`;

const StyledLineItemsTable = styled(LineItemsTable)`
  grid-area: lineitems;
`;

const BackButton = styled(OutlineButton)`
  display: flex;
  grid-area: back;
  align-self: center;
  justify-self: center;
  margin-top: 20px;

  line-height: 20px;

  svg {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }
`;
